import * as React from "react";
import { LocalizedDateTime, DateTimeAccuracy } from "../../misc/components/LocalizedDateTime";
import { CommentModals } from "../Shared/CommentsModals";
import { FixedCommentsSelectors } from "../Shared/FixedCommentsSelectors";
import {  StudyRelevantParameters } from "../Shared/PFTAPITypes";
import { PFTResultParameters } from "../Shared/PFTResultsParameters";
import RepeatabilityTable from "../Shared/RepeatabilityTable";
import { SelectedTestCommentsCount, SelectedSessionCommentsCount } from "../Shared/Selectors/FixedCommentSelectors";
import { TestAcceptabilityTable } from "../Shared/TestAcceptabilityTable";
import { AcceptabilityState, ToggleTestAcceptabilityAction } from "../Shared/UseAcceptability";
import { FixedCommentsState, LoadAnalysisSetOverreadAction, PageValidatedAction, ToggleSessionFixedCommentsVisibilityAction, ToggleTestFixedCommentsVisibilityAction } from "../Shared/UseFixedComments";
import { QualityAcceptabilityTable } from "../Shared/QualityAcceptabilityTable";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";


export interface OscillometryResultsTableProps {
    isViewOnly: boolean;
    isRejected: boolean;
    isSiteAnalysisSet: boolean;
    resultData: any;
    fixedCommentsState: FixedCommentsState;
    fixedCommentsDispatch;
    acceptabilityState: AcceptabilityState;
    acceptabilityDispatch;
    shouldValidateFixedComments: boolean;
    studyOscillometryRelevantParameters?: StudyRelevantParameters;
}

export const OscillometryResultsTable = (props: OscillometryResultsTableProps) => {
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const fetchOverreading = async () => {
            const analysisOverreading = await legacyClient.getOscillometryAnalysisSetOverreading(props.resultData.AnalysisSetId);

            props.fixedCommentsDispatch(LoadAnalysisSetOverreadAction.create(analysisOverreading));
        }

        fetchOverreading();
    }, [props.resultData.AnalysisSetId, props.fixedCommentsDispatch])

    return (
        <>
            <table id="OscillometryResultsTable" className="table table-striped">
                <thead>
                    <tr>
                        <th></th>
                        {
                            props.resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t =>
                                <th key={`TestHeader${t.TestId}`} style={{ color: "#000" }}>{t.TestNumber}</th>
                            )
                        }
                        <th>Best</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Time</td>
                        {
                            props.resultData.TestResults.sort((a, b) => a.TestNumber - b.TestNumber).map(t =>
                                <td key={`TestDateTime${t.TestId}`}>
                                    <LocalizedDateTime time={t.TestDateTime} accuracy={DateTimeAccuracy.Second} />
                                </td>
                            )
                        }
                        <td></td>
                    </tr>

                    <PFTResultParameters
                        isLoading={props.acceptabilityState.loadingBestValues}
                        results={props.resultData}
                        isRejected={props.isRejected}
                        studyRelevantParameters={props.studyOscillometryRelevantParameters}
                        showConfiguredParametersOnly={false}
                    />

                    <RepeatabilityTable
                        isLoading={props.acceptabilityState.loadingBestValues}
                        results={props.resultData}
                        isRejected={props.isRejected}
                        studyRelevantParameters={props.studyOscillometryRelevantParameters}
                        showConfiguredParametersOnly={false}
                    />
                    
                    <QualityAcceptabilityTable results={props.resultData} />

                    <TestAcceptabilityTable
                        isViewOnly={props.isViewOnly}
                        isLoading={props.acceptabilityState.loadingBestValues}
                        testResults={props.acceptabilityState.spirometryResults.TestResults}
                        grades={undefined}
                        displayTechnicianAcceptability={!props.isSiteAnalysisSet}
                        onAcceptabilityChange={(id, checked) =>
                            props.acceptabilityDispatch(
                                ToggleTestAcceptabilityAction.create(id, checked)
                            )
                        }
                    />

                    <FixedCommentsSelectors
                        testResults={props.resultData.TestResults}
                        selectedTestCommentsCount={SelectedTestCommentsCount(props.fixedCommentsState.testFixedComments)}
                        selectedSessionCommentsCount={
                            SelectedSessionCommentsCount(props.fixedCommentsState.sessionFixedComments)
                        }
                        onTestFixedCommentClick={testId =>
                            props.fixedCommentsDispatch(
                                ToggleTestFixedCommentsVisibilityAction.create(testId)
                            )
                        }
                        onSessionFixedCommentClick={() =>
                            props.fixedCommentsDispatch(
                                ToggleSessionFixedCommentsVisibilityAction.create()
                            )
                        }
                        addCommentsSuffix={true}
                    />

                </tbody>
            </table>
            <CommentModals
                editable={!props.isViewOnly}
                sessionFixedComments={props.fixedCommentsState.sessionFixedComments}
                testFixedComments={props.fixedCommentsState.testFixedComments}
                setIsPageValidated={(isValidated) => props.fixedCommentsDispatch(PageValidatedAction.create(isValidated))}
                isPageValid={props.fixedCommentsState.isPageValidated}
                dispatch={props.fixedCommentsDispatch}
            />
        </>
    );
}
